/**
 * Passo "Refeições do plano" do gerador: a nutricionista define quantas e quais
 * refeições o plano terá (a anamnese é só referência). Todas as metas por
 * refeição vêm do backend (diet-ai.php?action=preview) — a tela não faz conta
 * própria, para nunca divergir do que será gerado.
 * Ponto de partida: a distribuição salva para o paciente (se houver); senão o
 * padrão da nutricionista para o nº de refeições da anamnese.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { dietAIService, pacienteDistribuicaoService } from '../../services/api';
import { useToast } from '../../contexts/ToastContext';
import { mealIcon } from './helpers';
import { Ico, emo } from '../ui/Ico';

const C = {
  borda: '#ebe3da', texto: '#2f2a26', suave: '#736b64', ok: '#2f5236', erro: '#a8483f',
  aviso: '#8a5f1a', fundoAviso: '#fbf8f5', marca: '#D4A574', marcaEsc: '#b8895c',
};
const INPUT = { border: `1.5px solid ${C.borda}`, borderRadius: 8, padding: '0.4rem 0.5rem', fontSize: '0.85rem', fontFamily: 'inherit', background: '#ffffff', color: C.texto };
const BTN_ICONE = { border: `1px solid ${C.borda}`, background: '#ffffff', borderRadius: 6, cursor: 'pointer', padding: '0.2rem 0.45rem', fontSize: '0.8rem', color: C.suave };

const somente = (r) => ({ chave: r.chave, nome: r.nome, pct_kcal: Number(r.pct_kcal), horario: r.horario || '', perfil: r.perfil || null });

export function RefeicoesConfig({ patientId, serviceType, strategy, value, onChange, onValidChange }) {
  const [preview, setPreview]   = useState(null);   // resposta do backend (metas por refeição)
  const [erroApi, setErroApi]   = useState('');
  const [carregando, setCarr]   = useState(false);
  const [personalizada, setPers] = useState(false);
  const [salvandoPac, setSalvandoPac] = useState(false);
  const { showSuccess, showError } = useToast();
  const ultimaReq = useRef(0);

  const podePreview = patientId && serviceType && (serviceType !== 'planejamento_especializado' || strategy);

  const buscar = async (extra) => {
    const id = ++ultimaReq.current;
    setCarr(true);
    try {
      const r = await dietAIService.preview({ patient_id: patientId, service_type: serviceType, strategy: strategy || undefined, ...extra });
      if (id !== ultimaReq.current) return null;          // resposta velha: ignora
      if (!r?.success) { setErroApi(r?.error || 'Não foi possível calcular as metas.'); return null; }
      setErroApi(r.data.erro || '');
      setPreview(r.data);
      return r.data;
    } catch (e) {
      if (id === ultimaReq.current) setErroApi(e?.response?.data?.error || 'Não foi possível calcular as metas.');
      return null;
    } finally {
      if (id === ultimaReq.current) setCarr(false);
    }
  };

  // Paciente/programa mudou → carrega o padrão (nº da anamnese ou 6)
  useEffect(() => {
    if (!podePreview) return;
    buscar({}).then(d => { if (d && !d.erro) { onChange(d.refeicoes.map(somente)); setPers(false); } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId, serviceType, strategy]);

  // Edição da nutricionista → recalcula as metas (debounce)
  const assinatura = JSON.stringify(value || []);
  useEffect(() => {
    if (!podePreview || !personalizada || !value?.length) return;
    const t = setTimeout(() => { buscar({ refeicoes: value }); }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assinatura, personalizada]);

  const soma = useMemo(() => (value || []).reduce((t, r) => t + (Number(r.pct_kcal) || 0), 0), [value]);
  const somaOk = Math.abs(soma - 100) < 0.05;
  const valido = !!value?.length && somaOk && !erroApi && !carregando;
  useEffect(() => { onValidChange?.(valido); }, [valido, onValidChange]);

  const metasPorChave = useMemo(() => {
    const m = {};
    (preview?.refeicoes || []).forEach(r => { if (r.meta) m[r.chave] = r.meta; });
    return m;
  }, [preview]);

  const editar = (lista) => { setPers(true); onChange(lista); };
  const setCampo = (i, campo, v) => editar(value.map((r, j) => (j === i ? { ...r, [campo]: v } : r)));
  const mover = (i, d) => {
    const j = i + d; if (j < 0 || j >= value.length) return;
    const l = [...value]; [l[i], l[j]] = [l[j], l[i]]; editar(l);
  };
  const remover = (i) => editar(value.filter((_, j) => j !== i));
  const adicionar = (chave) => {
    const cat = preview?.catalogo?.[chave];
    const usadas = new Set(value.map(r => r.chave));
    let k = chave; let n = 1;
    if (!cat) { while (usadas.has(`extra_${n}`)) n++; k = `extra_${n}`; }
    editar([...value, { chave: k, nome: cat?.nome || 'Nova refeição', pct_kcal: 5, horario: cat?.horario || '', perfil: cat?.perfil || null }]);
  };
  const aplicarPadrao = async (n) => {
    if (personalizada && !window.confirm('Substituir as refeições que você ajustou pela distribuição padrão?')) return;
    const d = await buscar({ num_refeicoes: n });
    if (d && !d.erro) { onChange(d.refeicoes.map(somente)); setPers(false); }
  };

  // Distribuição da paciente: salva a configuração atual / apaga a salva
  const salvarParaPaciente = async () => {
    setSalvandoPac(true);
    try {
      const r = await pacienteDistribuicaoService.save(patientId, value.map(somente));
      if (!r?.success) { showError(r?.error || 'Não foi possível salvar.'); return; }
      showSuccess('Distribuição salva para esta paciente — vale nos próximos planos dela.');
      await buscar({ refeicoes: value });
    } catch (e) {
      showError(e?.response?.data?.error || 'Não foi possível salvar.');
    } finally { setSalvandoPac(false); }
  };
  const removerDaPaciente = async () => {
    if (!window.confirm('Apagar a distribuição salva desta paciente? Os próximos planos voltam a partir do padrão.')) return;
    try {
      const r = await pacienteDistribuicaoService.remove(patientId);
      if (!r?.success) { showError(r?.error || 'Não foi possível remover.'); return; }
      showSuccess('Distribuição da paciente removida.');
      const d = await buscar({});
      if (d && !d.erro) { onChange(d.refeicoes.map(somente)); setPers(false); }
    } catch (e) { showError(e?.response?.data?.error || 'Não foi possível remover.'); }
  };

  if (!podePreview) {
    return <p style={{ color: C.suave, fontSize: '0.85rem', margin: 0 }}>Selecione o paciente{serviceType === 'planejamento_especializado' ? ' e a estratégia' : ''} para montar as refeições.</p>;
  }

  const lim = preview?.limites || { min_refeicoes: 2, max_refeicoes: 8, pct_min: 2, pct_max: 60 };
  const anam = preview?.anamnese;
  const n = value?.length || 0;
  const disponiveis = Object.entries(preview?.catalogo || {}).filter(([k]) => !(value || []).some(r => r.chave === k));
  const tot = (preview?.refeicoes || []).reduce((t, r) => ({ kcal: t.kcal + (r.meta?.kcal || 0), p: t.p + (r.meta?.proteina || 0), c: t.c + (r.meta?.carboidratos || 0), g: t.g + (r.meta?.gordura || 0) }), { kcal: 0, p: 0, c: 0, g: 0 });

  return (
    <div>
      {/* O que o paciente relatou */}
      <div role="note" style={{ background: '#fbf8f5', border: '1px solid #edf1f6', borderRadius: 10, padding: '0.6rem 0.85rem', fontSize: '0.84rem', color: '#34495e', marginBottom: '0.9rem' }}>
        <Ico e="ℹ" /> {anam?.num_refeicoes
          ? <>Paciente relata <strong>{anam.num_refeicoes} refeição(ões)/dia</strong>{anam.horarios ? <> — horários: {anam.horarios}</> : null}.</>
          : 'A anamnese não informa quantas refeições o paciente faz.'}
        {' '}<span style={{ color: C.suave }}>A decisão final é sua.</span>
      </div>

      {/* De onde vem a distribuição + salvar para a paciente */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.9rem', fontSize: '0.82rem' }}>
        <span style={{ padding: '0.2rem 0.6rem', borderRadius: 999, fontWeight: 600,
                       background: preview?.origem === 'paciente' ? '#edf4ee' : '#f6f0ea',
                       color: preview?.origem === 'paciente' ? C.ok : '#5a3e2b' }}>
          {preview?.origem === 'paciente' ? <><Ico e="✓" sp />Distribuição da paciente</>
            : preview?.origem === 'personalizada' ? 'Ajustada neste plano' : 'Padrão do programa'}
        </span>
        {preview?.origem !== 'paciente' && (
          <button onClick={salvarParaPaciente} disabled={!value?.length || !somaOk || salvandoPac || carregando}
            style={{ ...BTN_ICONE, padding: '0.3rem 0.65rem', color: '#5a3e2b', fontWeight: 600 }}
            title="Os próximos planos desta paciente já começam com estas refeições">
            {salvandoPac ? 'Salvando…' : <><Ico e="💾" sp />Salvar para esta paciente</>}
          </button>
        )}
        {preview?.tem_distribuicao_paciente && (
          <button onClick={removerDaPaciente} style={{ ...BTN_ICONE, padding: '0.3rem 0.65rem' }}>
            Remover distribuição da paciente
          </button>
        )}
      </div>

      {/* Quantidade + restaurar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.9rem' }}>
        <span style={{ fontSize: '0.85rem', color: C.texto, fontWeight: 600 }}>Quantidade:</span>
        {[3, 4, 5, 6, 7].map(q => (
          <button key={q} onClick={() => aplicarPadrao(q)} aria-pressed={n === q}
            style={{ ...BTN_ICONE, minWidth: 36, padding: '0.35rem 0.6rem', fontWeight: 600, fontSize: '0.88rem',
                     background: n === q ? C.marca : '#ffffff', color: n === q ? '#ffffff' : C.texto, borderColor: n === q ? C.marca : C.borda }}>
            {q}
          </button>
        ))}
        <button onClick={() => aplicarPadrao(n || 6)} style={{ ...BTN_ICONE, marginLeft: 'auto' }} title="Volta aos percentuais padrão do programa para esta quantidade">
          ↺ Restaurar padrão do programa
        </button>
      </div>

      {/* Tabela de refeições */}
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.84rem', minWidth: 640 }}>
          <thead>
            <tr style={{ color: C.suave, textAlign: 'left' }}>
              <th scope="col" style={{ padding: '0.3rem', width: 56 }}><span className="sr-only">Ordem</span></th>
              <th scope="col" style={{ padding: '0.3rem' }}>Refeição</th>
              <th scope="col" style={{ padding: '0.3rem', width: 92 }}>Horário</th>
              <th scope="col" style={{ padding: '0.3rem', width: 78 }}>% kcal</th>
              <th scope="col" style={{ padding: '0.3rem', textAlign: 'right' }}>kcal</th>
              <th scope="col" style={{ padding: '0.3rem', textAlign: 'right' }}>P</th>
              <th scope="col" style={{ padding: '0.3rem', textAlign: 'right' }}>C</th>
              <th scope="col" style={{ padding: '0.3rem', textAlign: 'right' }}>G</th>
              <th scope="col" style={{ padding: '0.3rem', width: 32 }}><span className="sr-only">Remover</span></th>
            </tr>
          </thead>
          <tbody>
            {(value || []).map((r, i) => {
              const m = metasPorChave[r.chave];
              const pctFora = Number(r.pct_kcal) < lim.pct_min || Number(r.pct_kcal) > lim.pct_max;
              return (
                <tr key={r.chave} style={{ borderTop: `1px solid ${C.borda}` }}>
                  <td style={{ padding: '0.3rem', whiteSpace: 'nowrap' }}>
                    <button style={BTN_ICONE} onClick={() => mover(i, -1)} disabled={i === 0} aria-label={`Subir ${r.nome}`}>↑</button>{' '}
                    <button style={BTN_ICONE} onClick={() => mover(i, 1)} disabled={i === n - 1} aria-label={`Descer ${r.nome}`}>↓</button>
                  </td>
                  <td style={{ padding: '0.3rem' }}>
                    <span aria-hidden="true" style={{ marginRight: 4 }}>{emo(mealIcon(r.chave))}</span>
                    <input style={{ ...INPUT, width: 'calc(100% - 28px)' }} value={r.nome} maxLength={40}
                      aria-label={`Nome da refeição ${i + 1}`} onChange={e => setCampo(i, 'nome', e.target.value)} />
                  </td>
                  <td style={{ padding: '0.3rem' }}>
                    <input type="time" style={{ ...INPUT, width: 88 }} value={r.horario || ''} aria-label={`Horário de ${r.nome}`}
                      onChange={e => setCampo(i, 'horario', e.target.value)} />
                  </td>
                  <td style={{ padding: '0.3rem' }}>
                    <input type="number" min={lim.pct_min} max={lim.pct_max} step="1" inputMode="decimal"
                      style={{ ...INPUT, width: 64, borderColor: pctFora ? C.erro : C.borda }} value={r.pct_kcal}
                      aria-label={`Percentual de calorias de ${r.nome}`} aria-invalid={pctFora}
                      onChange={e => setCampo(i, 'pct_kcal', e.target.value === '' ? '' : Number(e.target.value))} />
                  </td>
                  <td style={{ padding: '0.3rem', textAlign: 'right', fontWeight: 600, color: C.texto }}>{m ? m.kcal : '–'}</td>
                  <td style={{ padding: '0.3rem', textAlign: 'right' }}>{m ? `${m.proteina} g` : '–'}</td>
                  <td style={{ padding: '0.3rem', textAlign: 'right' }}>{m ? `${m.carboidratos} g` : '–'}</td>
                  <td style={{ padding: '0.3rem', textAlign: 'right' }}>{m ? `${m.gordura} g` : '–'}</td>
                  <td style={{ padding: '0.3rem' }}>
                    <button style={{ ...BTN_ICONE, color: C.erro }} onClick={() => remover(i)} disabled={n <= lim.min_refeicoes}
                      aria-label={`Remover ${r.nome}`}><Ico e="✕" /></button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Adicionar */}
      {n < lim.max_refeicoes && (
        <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', alignItems: 'center', marginTop: '0.6rem', fontSize: '0.82rem' }}>
          <span style={{ color: C.suave }}>+ Adicionar:</span>
          {disponiveis.map(([k, c]) => (
            <button key={k} style={BTN_ICONE} onClick={() => adicionar(k)}>{emo(mealIcon(k))} {emo(c.nome)}</button>
          ))}
          <button style={BTN_ICONE} onClick={() => adicionar('outra')}><Ico e="🍴" sp />Outra…</button>
        </div>
      )}

      {/* Resumo e validação */}
      <div aria-live="polite" style={{ marginTop: '0.9rem', padding: '0.6rem 0.85rem', borderRadius: 10, fontSize: '0.85rem',
        background: somaOk && !erroApi ? '#edf4ee' : C.fundoAviso, color: somaOk && !erroApi ? C.ok : C.aviso }}>
        {!somaOk
          ? <>Soma dos percentuais: <strong>{Math.round(soma * 10) / 10}%</strong> — {soma < 100 ? `faltam ${Math.round((100 - soma) * 10) / 10}%` : `sobram ${Math.round((soma - 100) * 10) / 10}%`} para 100%.</>
          : erroApi
            ? <><Ico e="⚠" /> {erroApi}</>
            : <>Total: 100% <Ico e="✓" sp />· {tot.kcal} kcal · P {tot.p} g · C {tot.c} g · G {tot.g} g{carregando ? ' · recalculando…' : ''}</>}
      </div>
    </div>
  );
}
